import React, { useMemo } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { DeltaBadge } from "../../Components/dashboard/dashboardShared";
import { MacroCatalogDatasetItem, MacroCatalogTotals } from "./macroDataClient";

const number = (value: number): string => new Intl.NumberFormat("fr-FR").format(value);

const FREQUENCY_LABELS: Record<string, string> = {
	daily: "Journalier",
	weekly: "Hebdomadaire",
	monthly: "Mensuel",
	quarterly: "Trimestriel",
	annual: "Annuel",
	mixed: "Hebdomadaire + mensuel",
	unknown: "-"
};

/**
 * Ce qu'il reste à renseigner sur un sujet, en toutes lettres. Exporté pour être éprouvé
 * directement : c'est le texte que l'agent lit au survol, il ne doit jamais être approximatif.
 */
export function detailConfiguration(item: MacroCatalogDatasetItem): string {
	const parts: string[] = [];
	if ((item.missingUnits ?? 0) > 0) parts.push(`${item.missingUnits} sans unité`);
	if ((item.missingCharts ?? 0) > 0) parts.push(`${item.missingCharts} sans graphique`);
	return parts.length === 0
		? "Configuration complète."
		: `Indicateurs publiés mais incomplets : ${parts.join(", ")}.`;
}

/** Tuiles de totaux + tableau « état du portail » : une ligne par sujet, groupé par secteur. */
export const CatalogTable: React.FC<{
	totals: MacroCatalogTotals;
	datasets: MacroCatalogDatasetItem[];
}> = ({ totals, datasets }) => {
	const sectors = useMemo(() => {
		const bySector = new Map<string, { name: string; items: MacroCatalogDatasetItem[] }>();
		for (const item of [...datasets].sort((a, b) => a.sortOrder - b.sortOrder)) {
			const bucket = bySector.get(item.sector) ?? { name: item.sectorNameFr, items: [] };
			bucket.items.push(item);
			bySector.set(item.sector, bucket);
		}
		return [...bySector.values()];
	}, [datasets]);

	if (datasets.length === 0) {
		return (
			<p className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
				Aucun catalogue confirmé : validez puis confirmez un premier classeur.
			</p>
		);
	}

	return (
		<div className="space-y-4">
			{/* Grille inline auto-fit : immunisée contre l'écrasement de grid-cols-1 par la 2e feuille Tailwind. */}
			<div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
				{[
					["Sections", totals.datasets],
					["Indicateurs", totals.series],
					["Périodes", totals.periods],
					["Observations", totals.observations]
				].map(([label, value]) => (
					<div key={String(label)} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
						<p className="mt-1 text-3xl font-bold text-slate-900">{number(Number(value))}</p>
					</div>
				))}
			</div>

			<div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
				<table className="min-w-full text-left text-sm">
					<thead>
						<tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
							<th className="px-4 py-3">Section</th>
							<th className="px-4 py-3 text-right">Indicateurs</th>
							<th className="px-4 py-3 text-right">Périodes</th>
							<th className="px-4 py-3">Première</th>
							<th className="px-4 py-3">Dernière</th>
							<th className="px-4 py-3">Rythme</th>
							<th className="px-4 py-3 text-right">Δ veille</th>
						</tr>
					</thead>
					<tbody>
						{sectors.map(sector => (
							<React.Fragment key={sector.name}>
								<tr className="border-b border-slate-100 bg-slate-50">
									<td colSpan={7} className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#005CA9]">
										{sector.name}
									</td>
								</tr>
								{sector.items.map(item => (
									<tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
										<td className="px-4 py-3 font-semibold text-slate-800">
											{item.nameFr}
											{(item.seriesNeedingConfiguration ?? 0) > 0 && (
												// La donnée est publiée : la pastille dit ce qu'il reste à faire, elle
												// ne signale pas une indisponibilité.
												<span
													data-testid={`needs-config-${item.id}`}
													title={detailConfiguration(item)}
													className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
												>
													Configuration à compléter ({item.seriesNeedingConfiguration})
												</span>
											)}
										</td>
										<td className="px-4 py-3 text-right tabular-nums">{number(item.seriesCount)}</td>
										<td className="px-4 py-3 text-right tabular-nums">{number(item.periodCount)}</td>
										<td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">{item.firstPeriod ?? "-"}</td>
										<td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">
											<span className="inline-flex items-center gap-1">
												{item.lastPeriod ?? "-"}
												{item.lastPeriodAdvanced && (
													<FiArrowUpRight className="h-3.5 w-3.5 text-emerald-600" title="Période avancée depuis la veille" />
												)}
											</span>
										</td>
										<td className="whitespace-nowrap px-4 py-3 text-slate-600">
											{FREQUENCY_LABELS[item.frequency] ?? item.frequency}
										</td>
										<td className="px-4 py-3 text-right">
											{item.deltaObservations > 0 ? <DeltaBadge delta={item.deltaObservations} format={number} /> : <span className="text-slate-400">-</span>}
										</td>
									</tr>
								))}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
